import { tr } from '@/lib/i18n-core';
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { del, get, uploadWithProgress } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { ConfirmDialog, Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, Spinner } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

interface StudyImage {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  source: 'upload' | 'import';
  createdAt: string;
  url: string;
}

const formatSize = (bytes: number) => (bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`);

export function StudyImagesDialog({
  study,
  canEdit,
  onClose,
}: {
  study: { id: string; number: string; status: string; patient: { fullName: string; mrn: string }; studyType: { name: string } };
  canEdit: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [removing, setRemoving] = useState<{ id: string; fileName: string } | null>(null);
  const locked = ['verified', 'released'].includes(study.status);

  const images = useQuery({
    queryKey: ['radiology-images', study.id],
    queryFn: () => get<StudyImage[]>(`/radiology/studies/${study.id}/images`),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['radiology-images', study.id] });
    void queryClient.invalidateQueries({ queryKey: ['radiology-worklist'] });
  };

  const upload = async (file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    setProgress(0);
    try {
      await uploadWithProgress(`/radiology/studies/${study.id}/images`, form, setProgress);
      toast.success(tr('radiology', 'image_attached'));
      refresh();
    } catch (error) {
      toast.error(tr('radiology', 'could_not_attach_the_image'), describeError(error));
    } finally {
      setProgress(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const remove = useMutation({
    mutationFn: (id: string) => del(`/radiology/images/${id}`),
    onSuccess: () => {
      toast.success(tr('radiology', 'image_removed'));
      refresh();
    },
    onError: (error) => toast.error(tr('radiology', 'could_not_remove_the_image'), describeError(error)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      wide
      title={tr('radiology', 'images_x', { number: study.number })}
      description={`${study.patient.fullName} · ${study.patient.mrn} · ${study.studyType.name}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('radiology', 'close')}
          </Button>
          {canEdit && !locked && (
            <>
              <input
                ref={fileInput}
                type="file"
                accept="image/jpeg,image/png,image/webp,application/dicom,.dcm"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void upload(file);
                }}
              />
              <Button onClick={() => fileInput.current?.click()} disabled={progress !== null}>
                <Upload className="me-1 h-4 w-4" /> {progress !== null ? tr('radiology', 'uploading_x', { value: Math.round(progress * 100) }) : tr('radiology', 'add_an_image')}
              </Button>
            </>
          )}
        </>
      }
    >
      <div className="space-y-3">
        {locked && <Alert>{tr('radiology', 'the_report_is_released_images_are_kept_as_they')}</Alert>}
        <p className="text-xs text-muted-foreground">
          {tr('radiology', 'images_arrive_here_from_the_radiology')}
        </p>
        {images.isLoading && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner /> {tr('radiology', 'loading_images')}
          </p>
        )}
        {images.data && images.data.length === 0 && <p className="text-sm text-muted-foreground">{tr('radiology', 'no_images_attached_yet')}</p>}
        {images.data && images.data.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {images.data.map((image) => (
              <figure key={image.id} className="rounded-md border p-2">
                <a href={image.url} target="_blank" rel="noreferrer" className="block overflow-hidden rounded bg-muted">
                  {image.contentType.startsWith('image/') ? (
                    <img src={image.url} alt={image.fileName} className="h-40 w-full object-contain" />
                  ) : (
                    <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">{image.contentType}</div>
                  )}
                </a>
                <figcaption className="mt-2 flex items-start justify-between gap-2 text-xs">
                  <span>
                    <span className="block truncate font-medium" title={image.fileName}>
                      {image.fileName}
                    </span>
                    <span className="text-muted-foreground">
                      {formatSize(image.sizeBytes)} · {dateTime(image.createdAt)}
                    </span>
                  </span>
                  <span className="flex flex-col items-end gap-1">
                    <Badge variant={image.source === 'import' ? 'secondary' : 'muted'}>{image.source === 'import' ? tr('radiology', 'import_agent') : tr('radiology', 'uploaded')}</Badge>
                    {canEdit && !locked && (
                      <Button size="sm" variant="ghost" onClick={() => setRemoving(image)} loading={remove.isPending && remove.variables === image.id}>
                        {tr('radiology', 'remove')}
                      </Button>
                    )}
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </div>
      {removing && (
        <ConfirmDialog
          title={tr('radiology', 'remove')}
          subject={removing.fileName}
          destructive
          confirmLabel={tr('radiology', 'remove')}
          onConfirm={() => remove.mutateAsync(removing.id)}
          onClose={() => setRemoving(null)}
        />
      )}
    </Dialog>
  );
}
